import { RoomModel } from '../../types/protocols';
import { translateFieldValue } from '../../Helpers/archiveFieldValues';
import { extractSnapshotUpdatedBy, FIELD_TRANSLATIONS, translateFieldName } from '../../hooks/archive/useArchiveHistory';

/**
 * Reads one building's entry in `archive/changeLog/byDate/{date}/{buildingKey}`.
 *
 * The index is `{components|levels|rooms}/{entityKey}: true`. Entries written
 * before 2026-02-22 had no type segment (`{componentKey}: true` directly under
 * the building) and are read as components.
 */
export const componentKeysInDateIndex = (buildingEntry: unknown): string[] => {
    if (!buildingEntry || typeof buildingEntry !== 'object') return [];
    const entry = buildingEntry as Record<string, unknown>;

    const keys = new Set<string>();
    const components = entry.components;
    if (components && typeof components === 'object') {
        Object.keys(components).forEach(key => keys.add(key));
    }
    Object.entries(entry).forEach(([key, value]) => {
        if (value === true) keys.add(key);
    });
    return Array.from(keys);
};

/** `?date=` value of the page: a `YYYY-MM-DD` day, or null when absent or malformed. */
export const parseArchiveDateParam = (value: string | null): string | null =>
    value && /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`))
        ? value
        : null;

/** "17 okt" (short) or "17 oktober 2026" (long) for a `YYYY-MM-DD` day. */
export const formatArchiveDate = (date: string, style: 'short' | 'long'): string =>
    new Date(`${date}T00:00:00Z`).toLocaleDateString('sv-SE', {
        timeZone: 'UTC',
        day: 'numeric',
        month: style === 'short' ? 'short' : 'long',
        ...(style === 'long' ? { year: 'numeric' } : {}),
    });

/**
 * The value of a field in an archived component snapshot. The snapshot is the
 * component node verbatim, so a field is a ValueWithMetadata (`{value, ...}`,
 * or just `{status}` once cleared); older snapshots may hold a bare value.
 */
export const archivedFieldValue = (field: unknown): unknown =>
    field && typeof field === 'object' && !Array.isArray(field)
        ? (field as Record<string, unknown>).value
        : field;

/** A field's value as display text, or undefined when it is empty or not text. */
export const archivedFieldText = (field: unknown): string | undefined => {
    const value = archivedFieldValue(field);
    if (typeof value === 'string') return value.trim() ? value : undefined;
    if (typeof value === 'number') return String(value);
    return undefined;
};

/** The condition grade (`condition.value.value`), or undefined. */
export const archivedCondition = (field: unknown): number | undefined => {
    const grade = archivedFieldValue(archivedFieldValue(field));
    return typeof grade === 'number' ? grade : undefined;
};

/** One field change of an archived component, as a row in the day table. */
export interface ArchiveChangeRow {
    field: string;
    label: string;
    oldValue: string | null;
    newValue: string | null;
    /** UID of whoever last set the field that day, when known */
    changedBy: string | null;
}

/** Fields whose diff value is a stringified map or list, shown as a count. */
const COLLECTION_FIELDS = new Set([
    'photos', 'documents', 'blueprintLocations', 'mapLocations',
    'areaShapes', 'accessPositions', 'anchorPoints',
]);

const toText = (value: unknown): string | null =>
    typeof value === 'string' && value !== '' ? value : null;

/**
 * The rows of a component's `_diff` that the history pages show (the fields in
 * FIELD_TRANSLATIONS). A row without `changedBy` falls back to the field's
 * `updatedBy` in the snapshot. A deleted component is one row.
 */
export const changeRowsOf = <T extends { _archiveMetadata?: { deleted?: boolean }; _diff?: unknown }>(
    snapshot: T,
): ArchiveChangeRow[] => {
    if (snapshot._archiveMetadata?.deleted) {
        return [{ field: '_component', label: 'Komponent', oldValue: 'Fanns', newValue: 'Borttagen', changedBy: null }];
    }
    const changes = (snapshot._diff as { changes?: unknown } | undefined)?.changes;
    if (!Array.isArray(changes)) return [];

    return changes.flatMap((change): ArchiveChangeRow[] => {
        if (!change || typeof change !== 'object') return [];
        const c = change as Record<string, unknown>;
        if (typeof c.field !== 'string' || !(c.field in FIELD_TRANSLATIONS)) return [];
        return [{
            field: c.field,
            label: translateFieldName(c.field),
            oldValue: toText(c.oldValue),
            newValue: toText(c.newValue),
            changedBy: toText(c.changedBy) ?? extractSnapshotUpdatedBy((snapshot as Record<string, unknown>)[c.field]),
        }];
    });
};

/** A diff value as display text: translated, rooms named, collections counted. */
export const formatChangeValue = (
    field: string,
    value: string | null,
    rooms?: { [key: string]: RoomModel },
): string => {
    if (value === null) return '—';
    if (COLLECTION_FIELDS.has(field)) {
        try {
            const parsed: unknown = JSON.parse(value);
            if (parsed && typeof parsed === 'object') {
                return `${Object.keys(parsed).length} st`;
            }
        } catch {
            // A legacy plain-text value: show it as it is
        }
        return value;
    }
    return translateFieldValue(field, value, rooms);
};

/**
 * Where a component in the day table links to: its page, or its history page
 * when the snapshot is its deletion (the component page reads live components only).
 */
export const componentLinkOf = (snapshot: {
    _archiveMetadata: { buildingKey: string; componentKey: string; deleted?: boolean };
}): string => {
    const { buildingKey, componentKey, deleted } = snapshot._archiveMetadata;
    const page = `/building/${encodeURIComponent(buildingKey)}/component/${encodeURIComponent(componentKey)}`;
    return deleted ? `${page}/history` : page;
};
